/**
 * Progreso — DESKTOP.
 *
 * Maestro-detalle: la tabla de todos los ejercicios a la izquierda y, sin cambiar de pantalla, el
 * detalle del seleccionado a la derecha. Comparar dos ejercicios es un clic, no un ida y vuelta.
 * En el celular eso no existe: hay lista y después detalle, porque no hay ancho para las dos cosas.
 *
 * Cuál arranca seleccionado lo decide `useProgreso`, que lee el id de la URL: `/progreso/{id}` es
 * el contrato con el que "Ver su progresión" llega desde el registro.
 */

import type { ReactNode } from 'react';
import { formatearNumero, nombreMusculo } from '@mg/core';

import { MIN_SEMANAS_GRAFICO, semanasEnTexto, useProgreso } from '../features/useProgreso.ts';
import { Barras } from '../ui/charts.tsx';
import { Aviso, Cifra, Insignia, Pila, Vacio, estadosDeRatio } from '../ui/componentes.tsx';

export function ProgresoDesktop(): ReactNode {
  const p = useProgreso();

  if (p.datos.cargando) return <Vacio>Cargando…</Vacio>;
  if (p.filas.length === 0) return <Vacio>Todavía no hay series registradas.</Vacio>;

  return (
    <>
      {p.pedidoSinDatos && (
        <Aviso tono="info">
          Todavía no hay series registradas de {p.pedidoSinDatos.nombre}: la primera que anotes lo
          abre acá.
        </Aviso>
      )}

      <div className="d-rejilla d-rejilla--3">
        <section className="tarjeta">
          <span className="eyebrow">Empuje vs. tirón</span>
          <div style={{ marginTop: 'var(--e3)' }}>
            <Pila
              estados={estadosDeRatio(
                p.balances.empujeTiron.proporcion,
                16,
                p.balances.empujeTiron.desbalanceado ? 'ambar' : 'llena',
              )}
              alto={20}
            />
            <div className="fila fila--entre">
              <span className="nota--fina">
                <span className="lectura">{p.balances.empujeTiron.a}</span> empuje
              </span>
              <span className="nota--fina">
                <span className="lectura">{p.balances.empujeTiron.b}</span> tirón
              </span>
            </div>
          </div>
          <p className="nota--fina" style={{ marginTop: 6 }}>
            {p.balances.empujeTiron.detalle}
          </p>
        </section>

        <section className="tarjeta">
          <span className="eyebrow">Superior vs. pierna</span>
          <div style={{ marginTop: 'var(--e3)' }}>
            <Pila
              estados={estadosDeRatio(
                p.balances.superiorInferior.proporcion,
                16,
                p.balances.superiorInferior.desbalanceado ? 'ambar' : 'llena',
              )}
              alto={20}
            />
            <div className="fila fila--entre">
              <span className="nota--fina">
                <span className="lectura">{p.balances.superiorInferior.a}</span> superior
              </span>
              <span className="nota--fina">
                <span className="lectura">{p.balances.superiorInferior.b}</span> pierna
              </span>
            </div>
          </div>
          <p className="nota--fina" style={{ marginTop: 6 }}>
            {p.balances.superiorInferior.detalle}
          </p>
        </section>

        <section className="tarjeta">
          <span className="eyebrow">Cardio · comparable sólo a igual nivel</span>
          {p.cardio.porNivel.length === 0 ? (
            <p className="nota" style={{ marginTop: 'var(--e2)' }}>
              {p.cardio.sinNivel > 0
                ? `${p.cardio.sinNivel} ${p.cardio.sinNivel === 1 ? 'sesión' : 'sesiones'} sin nivel de resistencia: no hay tendencia posible.`
                : 'Sin cardio registrado.'}
            </p>
          ) : (
            <table className="tabla" style={{ marginTop: 'var(--e2)' }}>
              <thead>
                <tr>
                  <th className="num">Nivel</th>
                  <th className="num">Sesiones</th>
                  <th className="num">Δ distancia</th>
                </tr>
              </thead>
              <tbody>
                {p.cardio.porNivel.map((n) => (
                  <tr key={n.nivel}>
                    <td className="num">{n.nivel}</td>
                    <td className="num">{n.sesiones.length}</td>
                    <td className="num" style={{ color: n.mejora ? 'var(--agua)' : undefined }}>
                      {n.deltaDistanciaKm === null
                        ? '—'
                        : `${n.deltaDistanciaKm > 0 ? '+' : ''}${formatearNumero(n.deltaDistanciaKm, 2)} km`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {p.cardio.sinNivel > 0 && p.cardio.porNivel.length > 0 && (
            <p className="nota--fina" style={{ marginTop: 6, color: 'var(--ambar)' }}>
              {p.cardio.sinNivel} sin nivel, fuera de la tendencia.
            </p>
          )}
        </section>
      </div>

      <div className="d-rejilla" style={{ gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', alignItems: 'start' }}>
        <section className="tarjeta" style={{ padding: 0 }}>
          <div style={{ padding: 'var(--e4)' }}>
            <span className="eyebrow">Ejercicios · clic para ver el detalle</span>
          </div>
          <div className="desborda-x">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Ejercicio</th>
                  <th>Músculo</th>
                  <th className="num">Sesiones</th>
                  <th className="num">Peso máx</th>
                  <th className="num">e1RM</th>
                  {/* Columnas que sólo aparecen cuando alguna fila las llena: enteras en "—" no
                      informan nada y le sacan ancho al nombre del ejercicio. */}
                  {p.hayDeltaE1rm && <th className="num">Δ e1RM</th>}
                  {p.hayEstancamiento && <th>Estado</th>}
                </tr>
              </thead>
              <tbody>
                {p.filas.map((f) => (
                  <tr
                    key={f.ejercicio.id}
                    onClick={() => p.setSeleccionado(f.ejercicio.id)}
                    style={{
                      cursor: 'pointer',
                      background: f.ejercicio.id === p.seleccionado ? 'var(--ambar-tenue)' : undefined,
                    }}
                  >
                    <td>{f.ejercicio.nombre}</td>
                    <td className="apagado" style={{ fontSize: 12 }}>
                      {nombreMusculo(f.ejercicio.musculo_primario)}
                    </td>
                    <td className="num">{f.sesiones}</td>
                    <td className="num">
                      {f.records.pesoMax
                        ? `${formatearNumero(f.records.pesoMax.valor)} ${f.records.pesoMax.unidad}`
                        : '—'}
                    </td>
                    <td className="num">
                      {f.records.e1rmMax ? formatearNumero(f.records.e1rmMax.valor) : '—'}
                    </td>
                    {p.hayDeltaE1rm && (
                      <td className="num" style={{ color: (f.deltaE1rm ?? 0) > 0 ? 'var(--agua)' : undefined }}>
                        {f.deltaE1rm === null
                          ? '—'
                          : `${f.deltaE1rm > 0 ? '+' : ''}${formatearNumero(f.deltaE1rm)}`}
                      </td>
                    )}
                    {p.hayEstancamiento && (
                      <td>{f.estancamiento ? <Insignia tono="ambar">estancado</Insignia> : null}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="columna" style={{ gap: 'var(--e4)' }}>
          {p.detalle && (
            <>
              <section className="tarjeta">
                <h2 className="titulo">{p.detalle.ejercicio.nombre}</h2>
                <p className="nota--fina">
                  {nombreMusculo(p.detalle.ejercicio.musculo_primario)}
                  {p.detalle.ejercicio.musculos_secundarios.length > 0 &&
                    ` · también ${p.detalle.ejercicio.musculos_secundarios.map(nombreMusculo).join(', ')}`}
                </p>

                {p.detalle.estancamiento && (
                  <div style={{ marginTop: 'var(--e3)' }}>
                    <Aviso tono="atencion" titulo={`${p.detalle.estancamiento.sesiones} sesiones iguales`}>
                      Mismo peso y mismas reps desde el {p.detalle.estancamiento.desde}.
                    </Aviso>
                  </div>
                )}

                <div className="d-rejilla d-rejilla--2" style={{ marginTop: 'var(--e4)', gap: 'var(--e3)' }}>
                  <div>
                    <span className="eyebrow">Peso máximo</span>
                    <span className="cifra-grande">
                      {p.detalle.records.pesoMax ? formatearNumero(p.detalle.records.pesoMax.valor) : '—'}
                      <span className="unidad"> {p.detalle.records.pesoMax?.unidad ?? ''}</span>
                    </span>
                    <p className="nota--fina">{p.detalle.records.pesoMax?.fecha ?? ''}</p>
                  </div>
                  <div>
                    <span className="eyebrow">Reps máximas</span>
                    <Cifra valor={p.detalle.records.repsMax?.reps ?? null} decimales={0} />
                    <p className="nota--fina">{p.detalle.records.repsMax?.fecha ?? ''}</p>
                  </div>
                  <div>
                    <span className="eyebrow">e1RM ({p.formula})</span>
                    <Cifra valor={p.detalle.records.e1rmMax?.valor ?? null} unidad="kg" />
                  </div>
                  <div>
                    <span className="eyebrow">Mejor sesión</span>
                    <Cifra
                      valor={
                        p.detalle.records.volumenSesionMax ? p.detalle.records.volumenSesionMax.volumen / 1000 : null
                      }
                      unidad="t"
                      decimales={2}
                    />
                    <p className="nota--fina">{p.detalle.records.volumenSesionMax?.fecha ?? ''}</p>
                  </div>
                </div>

                {p.detalle.ejercicio.tipo === 'maquina' && (
                  <p className="nota--fina" style={{ marginTop: 'var(--e4)' }}>
                    {p.advertenciaE1rm}
                  </p>
                )}
              </section>

              <section className="tarjeta">
                <span className="eyebrow">Volumen por semana (kg)</span>
                {/* Con una o dos semanas el gráfico es un bloque gigante que no dice nada más que
                    la cifra. Se dice la cifra. */}
                {p.detalle.progresion.length < MIN_SEMANAS_GRAFICO ? (
                  <div className="columna" style={{ gap: 4, marginTop: 'var(--e2)' }}>
                    {semanasEnTexto(p.detalle.progresion).map((linea) => (
                      <span key={linea} className="lectura">
                        {linea}
                      </span>
                    ))}
                    <span className="nota--fina">
                      {p.detalle.progresion.length === 1
                        ? 'Con una sola semana todavía no hay tendencia que dibujar.'
                        : 'Con dos semanas todavía no hay tendencia que dibujar.'}
                    </span>
                  </div>
                ) : (
                  <Barras
                    datos={p.detalle.progresion.map((s) => ({ etiqueta: s.semana.slice(6), valor: s.volumenKg }))}
                    alto={150}
                    unidad="kg"
                  />
                )}
              </section>

              <section className="tarjeta" style={{ padding: 0 }}>
                <div style={{ padding: 'var(--e4) var(--e4) var(--e2)' }}>
                  <span className="eyebrow">Semana a semana</span>
                </div>
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Semana</th>
                      <th className="num">Peso máx</th>
                      <th className="num">Series</th>
                      <th className="num">Reps</th>
                      <th className="num">Volumen</th>
                      <th className="num">e1RM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...p.detalle.progresion].reverse().map((s) => (
                      <tr key={s.semana}>
                        <td className="lectura">{s.semana}</td>
                        <td className="num">{formatearNumero(s.pesoMaxKg)}</td>
                        <td className="num">{s.series}</td>
                        <td className="num">{s.repsTotales}</td>
                        <td className="num">{formatearNumero(s.volumenKg, 0)}</td>
                        <td className="num">{s.e1rmMaxKg === null ? '—' : formatearNumero(s.e1rmMaxKg)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            </>
          )}
        </aside>
      </div>
    </>
  );
}
